import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useState, useEffect } from 'react';

// Define user roles and their permissions
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager', 
  USER: 'user',
  VIEWER: 'viewer'
};

export const PERMISSIONS = {
  VIEW_ALL_LOGS: 'view_all_logs',
  VIEW_OWN_LOGS: 'view_own_logs',
  VIEW_TEAM_LOGS: 'view_team_logs',
  EXPORT_LOGS: 'export_logs',
  DELETE_LOGS: 'delete_logs',
  MODIFY_LOG_SETTINGS: 'modify_log_settings'
};

// Role-permission mapping
const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: [
    PERMISSIONS.VIEW_ALL_LOGS,
    PERMISSIONS.EXPORT_LOGS,
    PERMISSIONS.DELETE_LOGS,
    PERMISSIONS.MODIFY_LOG_SETTINGS
  ],
  [USER_ROLES.MANAGER]: [
    PERMISSIONS.VIEW_TEAM_LOGS,
    PERMISSIONS.VIEW_OWN_LOGS,
    PERMISSIONS.EXPORT_LOGS
  ],
  [USER_ROLES.USER]: [
    PERMISSIONS.VIEW_OWN_LOGS
  ],
  [USER_ROLES.VIEWER]: [
    PERMISSIONS.VIEW_ALL_LOGS
  ]
};

/**
 * Check if a user has a specific permission
 * @param {Array} permissions - Array of user permissions  
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (permissions, permission) => {
  if (!permissions || !Array.isArray(permissions)) return false;
  return permissions.includes(permission);
};

// Get user role from email domain or custom claims
const getUserRole = (user) => {
  if (!user) return null;
  
  // Check custom claims first (if implemented)
  if (user.customClaims?.role) {
    return user.customClaims.role;
  }
  
  // Default role assignment based on email
  const email = user.email?.toLowerCase();
  if (email?.includes('admin')) return USER_ROLES.ADMIN;
  if (email?.includes('manager')) return USER_ROLES.MANAGER;
  
  // Default to user role
  return USER_ROLES.USER;
};

// Hook for checking permissions
export const usePermissions = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);
  
  const userRole = getUserRole(user);
  const permissions = userRole ? ROLE_PERMISSIONS[userRole] || [] : [];
  
  const checkPermission = (permission) => {
    if (!userRole) return false;
    return permissions.includes(permission);
  };

  const canViewAllLogs = () => checkPermission(PERMISSIONS.VIEW_ALL_LOGS);
  const canViewOwnLogs = () => checkPermission(PERMISSIONS.VIEW_OWN_LOGS);
  const canViewTeamLogs = () => checkPermission(PERMISSIONS.VIEW_TEAM_LOGS);
  const canExportLogs = () => checkPermission(PERMISSIONS.EXPORT_LOGS);
  const canDeleteLogs = () => checkPermission(PERMISSIONS.DELETE_LOGS);
  const canModifySettings = () => checkPermission(PERMISSIONS.MODIFY_LOG_SETTINGS);

  return {
    user,
    userRole,
    permissions,
    loading,
    hasPermission: checkPermission,
    canViewAllLogs,
    canViewOwnLogs,
    canViewTeamLogs,
    canExportLogs,
    canDeleteLogs,
    canModifySettings
  };
};

// Filter logs based on user permissions
export const filterLogsByPermissions = (logs, user, userRole) => {
  if (!user || !userRole) return [];

  const permissions = ROLE_PERMISSIONS[userRole] || [];

  // Admin and viewers can see all logs
  if (permissions.includes(PERMISSIONS.VIEW_ALL_LOGS)) {
    return logs;
  }

  // Users can only see their own logs
  if (permissions.includes(PERMISSIONS.VIEW_OWN_LOGS) && !permissions.includes(PERMISSIONS.VIEW_TEAM_LOGS)) {
    return logs.filter(log => log.userEmail === user.email);
  }

  // Managers can see team logs (implement team logic as needed)
  if (permissions.includes(PERMISSIONS.VIEW_TEAM_LOGS)) {
    // For now, managers can see all logs - implement team filtering here
    return logs;
  }

  return [];
};
